import React from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { t } from 'ttag'
import { FormData } from '../../ChallengeAdmin'
import { FormGroup, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import YumSwitch from '../../../../components/YumSwitch/YumSwitch'
import { DivisionCategoryRow, ProgressType } from '../../../../services/api.types'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import makeStyles from '@material-ui/core/styles/makeStyles'

import { ReactComponent as IndividualInactiveIcon } from '../../../../assets/v3/icons/individual-inactive.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as CountryInactiveIcon } from '../../../../assets/v3/icons/country-inactive.svg'
import { ReactComponent as CollectiveTargetInactiveIcon } from '../../../../assets/v3/icons/collective-target-inactive.svg'

import { translateDivisionCategoryType } from '../../../TeamManagement/TeamManagement'
import { ActivityView } from '../../../Activity/store/ActivityReducer'
import { buildDivisionCategoryLeaderboardId, isActivityIndexType } from '../../../../common/helpers/challenges'
import { gray } from '../../../../styles/material-ui-theme-v2'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'

const useStyles = makeStyles({
  disableHover: {
    '&.MuiListItem-button:hover': {
      backgroundColor: gray,
      borderColor: 'transparent',
    },
  },
})

interface ChallengeStepLeaderboardsProps {
  register: UseFormRegister<FormData>
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  hasCountries: boolean
  divisionCategories: DivisionCategoryRow[]
  isLegacyChallenge: boolean
}

export default function ChallengeStepLeaderboards(props: ChallengeStepLeaderboardsProps) {
  const { watch, setValue, hasCountries, divisionCategories, isLegacyChallenge } = props
  const classes = useStyles()
  const [draggingId, setDraggingId] = React.useState<string | null>(null)
  const [showCountryHint, setShowCountryHint] = React.useState(false)

  // Stored enabled leaderboards (persisted when moving to next step).
  const leaderboards = watch('leaderboards') || []
  // Temporary ordered list with enabled flags (used only in this step).
  const leaderboardsDraft = watch('leaderboardsDraft') || []

  type LeaderboardOption = {
    id: string
    label: string
    icon: JSX.Element
    isCountry?: boolean
    isGroup?: boolean
  }

  const activityType = watch('activityType') as ProgressType | null
  const isActivityIndex = activityType ? isActivityIndexType(activityType) : false

  // Legacy challenges keep the original three tabs and do not allow ordering.
  // This mirrors the old UI for challenges without compact ranking view.
  const legacyOptions: LeaderboardOption[] = [
    { id: ActivityView.INDIVIDUAL, label: t`Individuals`, icon: <IndividualInactiveIcon /> },
    { id: ActivityView.GROUP, label: t`Teams`, icon: <GroupInactiveIcon />, isGroup: true },
    { id: ActivityView.COUNTRY, label: t`Countries`, icon: <CountryInactiveIcon />, isCountry: true },
  ]

  // Standard leaderboard options.
  // Collective is only available for activity index challenges.
  const standardOptions: LeaderboardOption[] = isLegacyChallenge
    ? legacyOptions
    : [
        { id: ActivityView.INDIVIDUAL, label: t`Individuals`, icon: <IndividualInactiveIcon /> },
        { id: ActivityView.GROUP, label: t`Teams`, icon: <GroupInactiveIcon />, isGroup: true },
        { id: ActivityView.COUNTRY, label: t`Countries`, icon: <CountryInactiveIcon />, isCountry: true },
        ...(!isActivityIndex
          ? [{ id: ActivityView.COLLECTIVE, label: t`Collective`, icon: <CollectiveTargetInactiveIcon /> }]
          : []),
      ]

  // Custom group leaderboard options are hidden for legacy challenges.
  const categoryOptions: LeaderboardOption[] = isLegacyChallenge
    ? []
    : divisionCategories
        .slice()
        .sort((a, b) => {
          const nameA = translateDivisionCategoryType(a, true)
          const nameB = translateDivisionCategoryType(b, true)
          return nameA.localeCompare(nameB)
        })
        .map((category) => {
          const categoryName = translateDivisionCategoryType(category, true)
          return {
            id: buildDivisionCategoryLeaderboardId(category.id),
            label: categoryName,
            // Use the group icon for now. This keeps the UI consistent.
            icon: <GroupInactiveIcon />,
          }
        })

  const allOptions: LeaderboardOption[] = [...standardOptions, ...categoryOptions]
  const optionsById = new Map(allOptions.map((option) => [option.id, option]))

  type LeaderboardDraftItem = {
    id: string
    enabled: boolean
  }

  const buildDraft = (draft: LeaderboardDraftItem[]): LeaderboardDraftItem[] => {
    // Keep the initial order aligned with the persisted goal leaderboards when available.
    // This ensures the form opens in the same order as the goal definition.
    const draftIds = draft.map((item) => item.id).filter((id) => optionsById.has(id))
    const goalIds = leaderboards.filter((id) => optionsById.has(id))
    const legacyOrderedIds = legacyOptions.map((option) => option.id)
    // Legacy challenges keep a fixed order to match the original experience.
    const orderedIds = isLegacyChallenge ? legacyOrderedIds : draftIds.length ? draftIds : goalIds

    // Append any missing options after the primary order.
    allOptions.forEach((option) => {
      if (!orderedIds.includes(option.id)) {
        orderedIds.push(option.id)
      }
    })

    const nextDraft = orderedIds.map((id) => {
      const existing = draft.find((item) => item.id === id)
      const option = optionsById.get(id)
      let enabled = existing ? existing.enabled : leaderboards.includes(id)

      // Disable country leaderboards when no countries are configured.
      if (!hasCountries && option?.isCountry) {
        enabled = false
      }

      return { id, enabled }
    })

    if (!nextDraft.some((item) => item.enabled)) {
      nextDraft[0] = { ...nextDraft[0], enabled: true }
    }

    return nextDraft
  }

  const normalizeDraft = React.useCallback(
    (draft: LeaderboardDraftItem[]) => buildDraft(draft),
    [allOptions, leaderboards, hasCountries, isLegacyChallenge],
  )

  React.useEffect(() => {
    const normalized = normalizeDraft(leaderboardsDraft)
    const isSame =
      normalized.length === leaderboardsDraft.length &&
      normalized.every((item, index) => {
        const existing = leaderboardsDraft[index]
        return existing && existing.id === item.id && existing.enabled === item.enabled
      })

    if (!isSame) {
      setValue('leaderboardsDraft', normalized, { shouldDirty: true, shouldTouch: true })
    }
  }, [leaderboardsDraft, normalizeDraft, setValue])

  const normalizedDraft: LeaderboardDraftItem[] = normalizeDraft(leaderboardsDraft)
  const enabledCount = normalizedDraft.filter((item) => item.enabled).length
  const onlyOneTabShown = enabledCount === 1

  // Update the temporary ordered list in the form state.
  const updateDraft = (nextDraft: LeaderboardDraftItem[]) => {
    setValue('leaderboardsDraft', nextDraft, { shouldDirty: true, shouldTouch: true })
  }

  const isToggleDisabled = (option: LeaderboardOption, isEnabled: boolean) => {
    if (isEnabled && onlyOneTabShown) return true
    if (option.isCountry && !hasCountries) return true
    return false
  }

  const handleToggle = (option: LeaderboardOption) => {
    const isEnabled = normalizedDraft.some((item) => item.id === option.id && item.enabled)
    const disabled = isToggleDisabled(option, isEnabled)
    if (disabled) {
      if (option.isCountry && !hasCountries) {
        setShowCountryHint(true)
      }
      return
    }
    setShowCountryHint(false)

    const nextDraft = normalizedDraft.map((item) => {
      if (item.id !== option.id) return item
      return { ...item, enabled: !item.enabled }
    })

    updateDraft(nextDraft)
  }

  // Simple reorder helper for drag-and-drop.
  const reorder = (list: LeaderboardDraftItem[], startIndex: number, endIndex: number): LeaderboardDraftItem[] => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    if (result.destination.index === result.source.index) return
    updateDraft(reorder(normalizedDraft, result.source.index, result.destination.index))
  }

  const renderOption = (
    option: LeaderboardOption,
    isEnabled: boolean,
    disableHover: boolean,
    showDragHandle?: boolean,
    dragHandleProps?: any,
  ) => {
    const toggleDisabled = isToggleDisabled(option, isEnabled)

    return (
      <>
        <ListItem
          role={undefined}
          dense
          button
          onClick={() => handleToggle(option)}
          className={disableHover ? classes.disableHover : undefined}
        >
          {showDragHandle && (
            <ListItemIcon {...(dragHandleProps || {})} style={{ minWidth: 32, cursor: 'grab' }}>
              <DragIndicatorIcon />
            </ListItemIcon>
          )}
          <ListItemIcon>{option.icon}</ListItemIcon>
          <ListItemText primary={option.label} />
          <ListItemIcon>
            <YumSwitch
              checked={isEnabled}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation()
                handleToggle(option)
              }}
              disabled={toggleDisabled}
            />
          </ListItemIcon>
        </ListItem>
        {option.isCountry && !hasCountries && showCountryHint && (
          <ErrorMessage>{t`Countries need to be added first`}</ErrorMessage>
        )}
      </>
    )
  }
  // Legacy challenges must keep the original ordering rules, so we disable drag-and-drop.
  const shouldAllowOrdering = !isLegacyChallenge

  return (
    <>
      <FormGroup>
        {/* Sortable list for modern challenges. */}
        {shouldAllowOrdering && (
          <DragDropContext
            onDragStart={(start) => setDraggingId(start.draggableId)}
            onDragEnd={(result) => {
              setDraggingId(null)
              handleDragEnd(result)
            }}
          >
            <Droppable droppableId="leaderboards">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {normalizedDraft.map((item, index) => {
                    const option = optionsById.get(item.id)
                    if (!option) return null
                    const isEnabled = item.enabled
                    const disableHover = !!draggingId && draggingId !== option.id
                    return (
                      <Draggable key={option.id} draggableId={option.id} index={index}>
                        {(draggableProvided) => (
                          // Apply draggable style on the same element as draggableProps to avoid jump.
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{ marginBottom: 12, ...draggableProvided.draggableProps.style }}
                          >
                            {renderOption(option, isEnabled, disableHover, true, draggableProvided.dragHandleProps)}
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        {/* Fixed order list for legacy challenges. */}
        {!shouldAllowOrdering && (
          <div>
            {normalizedDraft.map((item) => {
              const option = optionsById.get(item.id)
              if (!option) return null
              const isEnabled = item.enabled
              return (
                <div key={option.id} style={{ marginBottom: 12 }}>
                  {renderOption(option, isEnabled, false)}
                </div>
              )
            })}
          </div>
        )}
      </FormGroup>
      {isLegacyChallenge && (
        <div style={{ textAlign: 'center', opacity: 0.5, marginTop: '12px' }}>
          {t`Tip: Create a new challenge for more leaderboard options.`}
        </div>
      )}
    </>
  )
}
